import { ObservationService } from './../../shared/observation/observation.service';
import { ActivatedRoute, Params } from '@angular/router';
import { NotificationService } from './../../shared/notification/notification.service';
import { InvoiceUpsertResultDto } from './../../invoices/dto/invoice-upsert-result.dto';
import { PaymentTypeDto } from './../shared/payment-types/dto/payment-type.dto';
import { PaymentTypeService } from './../shared/payment-types/payment-type.service';
import { PayOffInvoicesDto, initializedPayOffInvoicesDto } from './../shared/dto/pay-off-invoices.dto';
import { TranslationService } from './../../shared/translation/translation.service';
import { LocalStorageService } from './../../shared/storage/local-storage.service';
import { Component, OnInit, Input, ViewEncapsulation, ViewChild } from '@angular/core';
import { InvoiceAdvancedSearch } from '../shared/invoice/invoice-advanced-search.model';
import { PagedResult } from '../../shared/model/generic-paged-result.model';
import { InvoiceAdvancedSearchResult } from '../shared/invoice/invoice-advanced-search-result.model';
import { InvoiceService } from '../shared/invoice/invoice.service';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { DatagridService, UtilService } from 'src/app/shared';
import { ConfirmationService, LazyLoadEvent, MenuItem } from 'primeng/api';
import { InvoiceAdvancedSearchExportOptionsDto } from '../shared/invoice/advanced-search-export-options.dto';
import { AdjustmentType } from '../shared/adjustment-types/adjustment-type.model';
import { InvoiceFinderContextService } from './shared/invoice-finder-context.service';

@Component({
  selector: 'wl-invoice-finder',
  templateUrl: './invoice-finder.component.html',
  styleUrls: ['./invoice-finder.component.less'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class InvoiceFinderComponent implements OnInit {
  @Input() public invoiceSearchParams: InvoiceAdvancedSearch = new InvoiceAdvancedSearch();
  public searching: boolean;
  // keep lazy load from fetching data on init
  public holdSearch = true;
  public get selectedInvoiceCount() {
    let count = 0;
    if (this.searchResult.Result) {
      count = this.searchResult.Result.reduce((acc, cur) => (cur.Selected ? ++acc : acc), 0);
    }
    return count;
  }
  public get selectAll() {
    return this._selectAll;
  }
  public set selectAll(value: boolean) {
    this._selectAll = value;
    this.selectedInvoices = [];
    for (const item of this.searchResult.Result) {
      item.Selected = this._selectAll;
    }
  }
  public actionsText: string;
  public actionsOptions: MenuItem[];
  public searchResult: PagedResult<Array<InvoiceAdvancedSearchResult>> = new PagedResult<Array<InvoiceAdvancedSearchResult>>();
  public generatingPayments = false;
  public writeOffInvoicesCheck = false;
  public isGenerating = false;
  public payOffDto: PayOffInvoicesDto;
  public paymentTypes = new Array<PaymentTypeDto>();
  public adjustmentTypes: Array<AdjustmentType>;
  public get selectedPaymentType() {
    return this._paymentType;
  }
  public set selectedPaymentType(value: PaymentTypeDto) {
    this._paymentType = value;

    // might have a null value if we're clearing out a previous config, so only do this if the value exists
    if (value) {
      this.payOffDto.PaymentTypeId = value.PaymentTypeId;
    }
  }
  public emailingInvoices = false;
  public actionsSearch = new InvoiceAdvancedSearch();
  selectedInvoices = [];

  private SEARCH_PARAMS_CACHE_KEY = 'InvoiceSelectorSearchParamsCache';
  private _selectAll = false;
  private _paymentType: PaymentTypeDto;

  constructor(
    private _datagridService: DatagridService,
    private _invoiceService: InvoiceService,
    private _navigationService: NavigationService,
    private _localStorageService: LocalStorageService,
    private _translationService: TranslationService,
    private _paymentTypeService: PaymentTypeService,
    private _notificationService: NotificationService,
    private _route: ActivatedRoute,
    private _observationService: ObservationService,
    private _utilService: UtilService,
    private _confirmationService: ConfirmationService,
    private _context: InvoiceFinderContextService
  ) {}

  ngOnInit() {
    this.initActionsOptions();
    this.initSearchParams();

    this._paymentTypeService.getPaymentTypes().then((data: Array<PaymentTypeDto>) => {
      this.paymentTypes = data;
    });

    this.payOffDto = initializedPayOffInvoicesDto();

    this._route.queryParams.subscribe((params: Params) => {
      if (params['applyPayment']) {
        this._observationService.applyBatchPayment();
      }
    });
  }

  lazyLoadSearchResults(e: LazyLoadEvent) {
    if (!this.holdSearch && this.invoiceSearchParams) {
      this._datagridService.mapDatagridPagingToPagedSearch(e, this.invoiceSearchParams);
      this.search();
    }
  }

  searchClicked() {
    this.holdSearch = false;
    this.invoiceSearchParams.PageNumber = 1;
    this.search();
    this.selectedInvoices = [];
    this._selectAll = false;
  }

  clearSearch() {
    this._localStorageService.removeItem(this.SEARCH_PARAMS_CACHE_KEY);
    this.initSearchParams();
    this.searchResult = new PagedResult();
    this.selectedInvoices = [];
    this._selectAll = false;
  }

  pageClicked(page: number) {
    this.search();
  }

  navigateToInvoice(invoiceNum: number) {
    this._navigationService.navigateToInvoiceEdit(invoiceNum);
  }

  search() {
    this.searching = true;
    this._localStorageService.setObject(this.SEARCH_PARAMS_CACHE_KEY, this.invoiceSearchParams);
    this._invoiceService.advancedSearch(this.invoiceSearchParams).then((data) => {
      this.searchResult = data;
      this.invoiceSearchParams.PageSize = data.PageSize;
      this.invoiceSearchParams.TotalCount = data.TotalCount;
      this.invoiceSearchParams.TotalPages = data.TotalPages;
      this.searchResult.Result.forEach((x) => (x.Selected = this._selectAll != this.selectedInvoices.some((si) => si == x.InvoiceNum)));
      this.searching = false;
    });
  }

  pageSizeChanged() {
    if (!this.holdSearch) {
      this.invoiceSearchParams.PageNumber = 1;
      this.search();
    }
  }

  public cancelPaymentGenerate() {
    this.payOffDto = initializedPayOffInvoicesDto();
    this.selectedPaymentType = null;
    this.generatingPayments = false;
  }

  public generatePayments() {
    if (!this._paymentType) {
      this._notificationService.error(this._translationService.getInstant('revenue.invoice-selector.actions.payment-type-required'));
      return;
    }
    this.isGenerating = true;
    this.getSelectedInvoices().then((invoices: Array<number>) => {
      this.payOffDto.InvoiceNumbers = invoices;
      this._invoiceService
        .payOffInvoices(this.payOffDto)
        .then((result: InvoiceUpsertResultDto) => {
          if (result.UpsertSuccessful) {
            for (const message of result.Messages) {
              this._notificationService.success(message);
            }
            this.isGenerating = false;
            this.cancelPaymentGenerate();
          }
        })
        .catch((error) => {
          this._notificationService.httpError(error);
          this.isGenerating = false;
        });
    });
  }

  private initSearchParams() {
    if (this._localStorageService.getObject(this.SEARCH_PARAMS_CACHE_KEY, undefined)) {
      this.invoiceSearchParams = this._localStorageService.getObject(this.SEARCH_PARAMS_CACHE_KEY);
      this.holdSearch = false;
    } else {
      this.invoiceSearchParams = new InvoiceAdvancedSearch();
      this.invoiceSearchParams.PageSize = 20;
    }
  }

  private initActionsOptions() {
    let p1 = this._translationService.get('revenue.invoice-selector.actions.label');
    let p2 = this._translationService.get('revenue.invoice-selector.actions.view-print-selected');
    let p3 = this._translationService.get('revenue.invoice-selector.actions.email-selected');
    let p4 = this._translationService.get('revenue.invoice-selector.actions.generate-payments');
    let p5 = this._translationService.get('revenue.invoice-selector.actions.export-invoices');
    let p6 = this._translationService.get('revenue.invoice-selector.actions.disable-portal-payments');
    let p7 = this._translationService.get('revenue.invoice-selector.actions.write-off-invoices');
    let p8 = this._translationService.get('revenue.invoice-selector.actions.rebalance-invoices');

    Promise.all([p1, p2, p3, p4, p5, p6, p7, p8]).then((data: string[]) => {
      this.actionsText = data[0];
      this.actionsOptions = [
        {
          label: data[1],
          command: () => {
            this.viewPrintSelected();
          },
        },
        {
          label: data[2],
          command: () => {
            this.emailInvoices();
          },
        },
        {
          label: data[3],
          command: () => {
            this.generatingPayments = true;
          },
        },
        {
          label: data[4],
          command: () => {
            this.export();
          },
        },
        {
          label: data[5],
          command: () => {
            this.confirmDisablePortalPayments();
          },
        },
        {
          label: data[6],
          command: () => {
            this.getSelectedInvoices().then((invoices: Array<number>) => {
              this._context.setSelectedInvoices(invoices);
              this._context.setWriteOffOpen(true);
            });
          },
        },
        {
          label: data[7],
          visible: true,
          command: () => {
            this.confirmRebalanceSelected();
          },
        },
      ];
    });
  }

  confirmRebalanceSelected() {
    this._confirmationService.confirm({
      key: 'rebalanceConfirmation',
      message: this._translationService.getInstant('revenue.invoice-selector.actions.confirm-rebalance'),
      accept: () => this.rebalanceSelected(),
      reject: () => this._confirmationService.close(),
    });
  }

  async rebalanceSelected() {
    const invoices = await this.getSelectedInvoices();
    await this._invoiceService.rebalanceInvoices(invoices);
    this._notificationService.translateInfo('revenue.invoice-selector.actions.rebalance-started');
  }

  async export() {
    const searchParams = this._utilService.clone(this.invoiceSearchParams);
    if (this.selectedInvoices.length) {
      searchParams.InvoiceNums = await this.getSelectedInvoices();
    }

    this._invoiceService.exportSearch({ SearchParams: searchParams } as InvoiceAdvancedSearchExportOptionsDto).then((data) => {
      this._navigationService.popupSafeNavigate(data);
    });
  }

  public getSelectedInvoices(): Promise<Array<number>> {
    if (!this.selectAll) {
      return Promise.resolve(this.selectedInvoices);
    } else {
      const searchParams = this._utilService.clone(this.invoiceSearchParams);
      searchParams.PageNumber = 0;
      searchParams.PageSize = 0;
      return this._invoiceService.advancedSearch(searchParams).then((data: PagedResult<Array<InvoiceAdvancedSearchResult>>) => {
        const invoiceNums = data.Result.filter((x) => !this.selectedInvoices.some((si) => x.InvoiceNum == si)).map((x) => x.InvoiceNum);
        return Promise.resolve(invoiceNums);
      });
    }
  }

  private getSelectedInvoicesAsSearchDto(): InvoiceAdvancedSearch {
    if (!this.selectAll) {
      return new InvoiceAdvancedSearch({ InvoiceNums: this.selectedInvoices, PageNumber: 0, PageSize: 0 });
    } else {
      const searchParams: InvoiceAdvancedSearch = this._utilService.clone(this.invoiceSearchParams);
      searchParams.ExcludeInvoiceNums = this.selectedInvoices;
      searchParams.PageNumber = 0;
      searchParams.PageSize = 0;
      return searchParams;
    }
  }

  private emailInvoices() {
    this.actionsSearch = new InvoiceAdvancedSearch();
    if (!this.selectAll && this.selectedInvoices.length == 0) {
      this._confirmationService.confirm({
        key: 'selectAnInvoiceDialog',
        message: this._translationService.getInstant('revenue.invoice-selector.actions.select-an-invoice-body'),
        accept: () => this._confirmationService.close(),
      });
      return;
    }
    this.getSelectedInvoices().then((invoices: Array<number>) => {
      this.actionsSearch.InvoiceNums = invoices;

      this.emailingInvoices = true;
    });
  }

  private viewPrintSelected() {
    const invoiceCount = this.selectAll ? this.searchResult.TotalCount - this.selectedInvoices.length : this.selectedInvoices.length;
    if (invoiceCount >= 500) {
      this._confirmationService.confirm({
        key: 'maxInvoicesAlert',
        message: this._translationService.getInstant('revenue.invoice-selector.actions.max-invoices-message'),
        accept: () => this._confirmationService.close(),
      });
    } else {
      this.getSelectedInvoices().then((invoices: Array<number>) => {
        this._navigationService.navigateToInvoiceReportForMultipleNumbers(invoices);
      });
    }
  }

  private confirmDisablePortalPayments() {
    this._confirmationService.confirm({
      key: 'disablePortalPaymentsConfirmation',
      message: this._translationService.getInstant('revenue.invoice-selector.actions.confirm-disable-portal-payments'),
      accept: () => this.disablePortalPayments(),
      reject: () => this._confirmationService.close(),
    });
  }

  private disablePortalPayments() {
    const searchDto = this.getSelectedInvoicesAsSearchDto();
    this._invoiceService
      .bulkSetDisableOnlinePayment({
        SearchParams: searchDto,
        DisableOnlinePaymentValue: true,
      })
      .then(() => {
        this._notificationService.translateSuccess('revenue.invoice-selector.actions.disable-portal-payments-success');
      });
  }

  invoiceSelectedChanged(invoice: InvoiceAdvancedSearchResult) {
    if (this._selectAll != invoice.Selected) {
      this.selectedInvoices.push(invoice.InvoiceNum);
    } else {
      const index = this.selectedInvoices.indexOf(invoice.InvoiceNum);
      this.selectedInvoices.splice(index, 1);
    }
  }
}
